
import { useState, useEffect, useRef, useCallback } from 'react';

interface UseTrialCountdownProps {
  expiresAt: string;
  isActive: boolean;
}

export const useTrialCountdown = ({ expiresAt, isActive }: UseTrialCountdownProps) => {
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);

  const formatTimeRemaining = useCallback((ms: number) => {
    const safeMs = Math.max(0, ms);
    const hours = Math.floor(safeMs / (1000 * 60 * 60));
    const minutes = Math.floor((safeMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((safeMs % (1000 * 60)) / 1000);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    
    if (!isActive || !expiresAt) {
      clearTimer();
      setTimeRemaining(0);
      return;
    }

    const expirationTime = new Date(expiresAt).getTime();
    
    const updateTimer = () => {
      if (!isMountedRef.current) {
        clearTimer();
        return;
      }

      const now = Date.now();
      const remaining = Math.max(0, expirationTime - now);
      setTimeRemaining(remaining);

      if (remaining === 0) {
        clearTimer();
      }
    };

    // Update immediately
    updateTimer();
    
    // Then set interval for updates every second
    timerRef.current = setInterval(updateTimer, 1000);

    return () => {
      clearTimer();
    };
  }, [expiresAt, isActive, clearTimer]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      clearTimer();
    };
  }, [clearTimer]);

  return {
    timeRemaining,
    formattedTime: formatTimeRemaining(timeRemaining),
    isExpired: timeRemaining === 0
  };
};
